import { ArrowDropDown as ArrowDropDownIcon } from 'components/icons';
import { buttonStyle } from 'components/buttons/buttonStyle';
import CircularLoader from 'components/CircularLoader';
import InfoTooltip from 'components/InfoTooltip';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useUpdateInvoiceAutoGenerationMutation } from 'store/api/tenants.ts';
import { DayOfWeek, TenantConfigurationDto } from 'types/tenants.ts';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations.ts';
import {
    DATE_BASED_INVOICING_INTERVALS,
    INVOICE_AUTO_GENERATION_SCHEDULE_SECTION_ID,
    ordinal,
    WEEK_BASED_INVOICING_INTERVALS,
} from 'utils/invoiceAutoGenerationSchedule';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { useHashScroll } from 'utils/useHashScroll';
import WeekdayPicker from './WeekdayPicker.tsx';

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, index) => index + 1);

/** The hovered link: darker than the primary green, as on the selected weekday. */
const PRIMARY_DARK_HOVER_TEXT =
    'hover:text-[color-mix(in_srgb,var(--color-primary)_80%,black)] new:hover:text-primary-strong';

const PANEL_HEADING_CLASS = 'text-fg-secondary text-xs leading-none tracking-[.06em] uppercase';

type IntervalDayPickerRowProps = {
    label: string;
    day: number;
    disabled: boolean;
    onSelectDay: (day: number) => void;
};

/**
 * One "interval type → day of month" row: interval name on the left, a dropdown-style trigger on
 * the right opening the month-board popover below it. Owns its popover's open state so opening the
 * picker never re-renders (and thus never remounts) the surrounding layout.
 */
const IntervalDayPickerRow = ({ label, day, disabled, onSelectDay }: IntervalDayPickerRowProps): JSX.Element => {
    const [open, setOpen] = useState(false);

    return (
        <div className="flex items-center justify-between gap-4">
            <p className="text-body2">{label}</p>
            {/* Modal: the page does not scroll under the month board while it is open. */}
            <Popover open={open} onOpenChange={setOpen} modal>
                <PopoverTrigger
                    disabled={disabled}
                    aria-label={`${label}: ${ordinal(day)}`}
                    className="border-divider bg-paper data-popup-open:border-primary data-popup-open:ring-primary/15 flex w-[196px] cursor-pointer items-center justify-between rounded-[10px] border px-3.5 py-2.5 text-left disabled:pointer-events-none data-popup-open:ring-3"
                >
                    <span className="text-body1">
                        <span className="font-semibold">{ordinal(day)}</span>{' '}
                        <span className="text-body2 text-fg-secondary">
                            {t('financial-conditions.interval-date-caption')}
                        </span>
                    </span>
                    <ArrowDropDownIcon className="text-fg-secondary" />
                </PopoverTrigger>
                <PopoverContent
                    align="end"
                    sideOffset={6}
                    positionerClassName="z-(--z-modal)"
                    className="border-divider w-[322px] gap-0 rounded-[13px] border p-[15px] shadow-[0_16px_40px_-10px_rgba(20,22,25,0.3)] ring-0"
                >
                    <div className="mx-0.5 mb-3">
                        <span className="text-fg-secondary new:text-label new:font-semibold new:tracking-[0.06em] text-xs leading-none tracking-[0.08333em] uppercase">
                            {`${label} — ${t('financial-conditions.interval-date-picker-title')}`}
                        </span>
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                        {DAYS_OF_MONTH.map((dayOption) => {
                            const selected = dayOption === day;
                            return (
                                <button
                                    type="button"
                                    key={dayOption}
                                    aria-pressed={selected}
                                    onClick={() => {
                                        onSelectDay(dayOption);
                                        setOpen(false);
                                    }}
                                    className={cn(
                                        'h-9 cursor-pointer rounded-lg text-sm',
                                        selected
                                            ? 'bg-primary text-primary-foreground font-semibold'
                                            : 'text-fg hover:ring-primary/40 hover:ring-[1.5px] hover:ring-inset'
                                    )}
                                >
                                    {dayOption}
                                </button>
                            );
                        })}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

/**
 * Tenant-wide invoice autogeneration schedule, configured per invoicing interval type. Card with
 * two panels: date-based intervals each pick a day of month via a month-board popover, week-based
 * intervals each pick a weekday via a pill row.
 */
const InvoiceAutoGenerationScheduleControl = ({ tenantConfiguration }: Props): JSX.Element => {
    const { classifiers } = useClassifiers({ codes: ['INVOICING_INTERVAL_TYPE'] });
    const [updateInvoiceAutoGeneration, { isLoading: isSaving }] = useUpdateInvoiceAutoGenerationMutation();
    const hasUpdatePermission = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);
    // Scroll to this section when opened via a #invoice-auto-generation-schedule deep link.
    // The card is taller than half the viewport, so align its top instead of centering.
    useHashScroll({ block: 'start' });

    const [daysOfMonth, setDaysOfMonth] = useState<Record<string, number>>(
        tenantConfiguration.invoiceAutoGenerationDaysOfMonth
    );
    const [daysOfWeek, setDaysOfWeek] = useState<Record<string, DayOfWeek>>(
        tenantConfiguration.invoiceAutoGenerationDaysOfWeek
    );

    const intervalLabel = useCallback(
        (intervalCode: string) =>
            getClassifierText(classifiers['INVOICING_INTERVAL_TYPE'], intervalCode) || intervalCode,
        [classifiers]
    );

    const applyMonthlyDayToAll = () => {
        setDaysOfMonth((current) => {
            const monthlyDay = current['MONTHLY'] ?? 1;
            return Object.fromEntries(DATE_BASED_INVOICING_INTERVALS.map((intervalCode) => [intervalCode, monthlyDay]));
        });
    };

    return (
        <div
            id={INVOICE_AUTO_GENERATION_SCHEDULE_SECTION_ID}
            className="border-divider bg-paper new:border-0 new:shadow-card new:outline-hidden max-w-[1120px] scroll-mt-20 overflow-hidden rounded-2xl border shadow-[0_20px_52px_-16px_rgba(20,22,25,0.12)]"
        >
            <div className="border-divider border-b px-[30px] pt-[26px] pb-[22px]">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.invoice-auto-generation-schedule" />
                </h3>
                <p className="text-body2 text-fg-secondary mt-1">
                    <FormattedMessage id="configuration.invoice-auto-generation-schedule-subtitle" />
                </p>
            </div>

            <div className="grid grid-cols-[1fr_1px_1fr]">
                {/* Interval date panel */}
                <div className="px-[30px] pt-6 pb-[26px]">
                    <div className="mb-5 flex items-center gap-1.5">
                        <span className={PANEL_HEADING_CLASS}>{t('financial-conditions.interval-date')}</span>
                        <InfoTooltip text="financial-conditions.tooltips.interval-date" />
                    </div>
                    <div className="flex flex-col gap-4">
                        {DATE_BASED_INVOICING_INTERVALS.map((intervalCode) => (
                            <IntervalDayPickerRow
                                key={intervalCode}
                                label={intervalLabel(intervalCode)}
                                day={daysOfMonth[intervalCode] ?? 1}
                                disabled={!hasUpdatePermission}
                                onSelectDay={(day) =>
                                    setDaysOfMonth((current) => ({
                                        ...current,
                                        [intervalCode]: day,
                                    }))
                                }
                            />
                        ))}
                    </div>
                    <div className="border-divider mt-5 border-t pt-4">
                        <button
                            type="button"
                            onClick={applyMonthlyDayToAll}
                            disabled={!hasUpdatePermission}
                            className={cn(
                                'text-primary inline-flex cursor-pointer items-center gap-1.5 rounded-md text-[13px] font-medium disabled:pointer-events-none',
                                PRIMARY_DARK_HOVER_TEXT
                            )}
                        >
                            <span className="text-[15px] leading-none">⤵</span>
                            {t('configuration.apply-monthly-day-to-all')}
                        </button>
                    </div>
                </div>

                <div className="bg-divider" />

                {/* Interval weekday panel */}
                <div className="px-[30px] pt-6 pb-[26px]">
                    <div className="mb-5 flex items-center gap-1.5">
                        <span className={PANEL_HEADING_CLASS}>{t('financial-conditions.interval-weekday')}</span>
                        <InfoTooltip text="financial-conditions.tooltips.interval-weekday" />
                    </div>
                    <div className="flex flex-col gap-5">
                        {WEEK_BASED_INVOICING_INTERVALS.map((intervalCode) => {
                            const weekday = daysOfWeek[intervalCode] ?? DayOfWeek.MONDAY;
                            return (
                                <div key={intervalCode}>
                                    <p className="text-body2 mb-2.5">{intervalLabel(intervalCode)}</p>
                                    <WeekdayPicker
                                        label={intervalLabel(intervalCode)}
                                        value={weekday}
                                        disabled={!hasUpdatePermission}
                                        onChange={(day) =>
                                            setDaysOfWeek((current) => ({
                                                ...current,
                                                [intervalCode]: day,
                                            }))
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="border-divider bg-grey-50 flex items-center justify-end gap-2.5 border-t px-[30px] py-[18px]">
                <Button
                    {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'relative h-8')}
                    disabled={!hasUpdatePermission || isSaving}
                    onClick={() => {
                        void updateInvoiceAutoGeneration({
                            isInvoiceAutoGenerationEnabled: tenantConfiguration.isInvoiceAutoGenerationEnabled,
                            invoiceAutoGenerationDaysOfMonth: daysOfMonth,
                            invoiceAutoGenerationDaysOfWeek: daysOfWeek,
                        })
                            .unwrap()
                            // Re-seed from the response so the card reflects the server-normalized
                            // schedule (the state is seeded from props only on mount).
                            .then((configuration) => {
                                setDaysOfMonth(configuration.invoiceAutoGenerationDaysOfMonth);
                                setDaysOfWeek(configuration.invoiceAutoGenerationDaysOfWeek);
                            })
                            .catch(() => {
                                // the global API handler already shows the error toast
                            });
                    }}
                >
                    {/* While saving, the spinner takes the label's place and the button keeps its width. */}
                    {isSaving ? <CircularLoader className="z-auto [&>span]:size-4 [&>span]:border-2" /> : null}
                    <span className={cn(isSaving && 'invisible')}>
                        <FormattedMessage id="save" />
                    </span>
                </Button>
            </div>
        </div>
    );
};

export default InvoiceAutoGenerationScheduleControl;
